import { defaultLegs, defaultTrim, setAssemblyLegs, setAssemblyTrim, type AssemblyLegs, TRIM_LABEL, TRIM_SIDES, type AssemblyInstance, type AssemblyTrim, type TrimSide } from '@scs/domain-engine';
import { MmInput } from '../lib/ui';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

/** Legs ("nogice"): on / off, height, pattern, largest spacing, inset from the edges, diameter. */
export function LegProperties({ inst, exec, disabled }: { inst: AssemblyInstance; exec: Exec; disabled: boolean }) {
  const legs = inst.legs;
  const set = (label: string, next: AssemblyLegs | null) =>
    exec(label, (d) => {
      const a = d.assemblies.find((x) => x.id === inst.id);
      if (a) setAssemblyLegs(a, next);
    });
  const upd = (label: string, patch: Partial<AssemblyLegs>) => legs && set(`Nogice: ${label}`, { ...legs, ...patch });
  return (
    <div className="prop-group">
      <label className="opt-row trim-check">
        <input type="checkbox" checked={!!legs} disabled={disabled} onChange={(e) => set(e.target.checked ? 'Nogice: uključene' : 'Nogice: isključene', e.target.checked ? defaultLegs(inst) : null)} />
        <strong>Nogice</strong>
      </label>
      {legs && (
        <div className="trim-fields">
          <div className="prop-row">
            <label>Visina</label>
            <MmInput label="Visina nogica" value={legs.height} min={1} max={500} disabled={disabled} onCommit={(v) => upd('visina', { height: v })} />
          </div>
          <div className="prop-row">
            <label title="Automatski: uglovi, ispod svake vertikalne pregrade i dodatne gdje je razmak veći od najvećeg razmaka (po širini i po dubini)">Raspored</label>
            <select className="input" disabled={disabled} value={legs.pattern} onChange={(e) => upd('raspored', { pattern: e.target.value as AssemblyLegs['pattern'] })}>
              <option value="auto">uglovi + pregrade + razmak</option>
              <option value="corners">samo uglovi</option>
            </select>
          </div>
          {legs.pattern === 'auto' && (
            <div className="prop-row">
              <label title="Ako su dvije susjedne nogice dalje od ovoga, dodaju se nogice između (ravnomjerno)">Najveći razmak</label>
              <MmInput label="Najveći razmak nogica" value={legs.maxSpacing} min={100} max={3000} disabled={disabled} onCommit={(v) => upd('najveći razmak', { maxSpacing: v })} />
            </div>
          )}
          <div className="prop-row">
            <label title="Centar nogice od ivice korpusa">Od ivice</label>
            <MmInput label="Odmak nogica od ivice" value={legs.inset} min={0} max={500} disabled={disabled} onCommit={(v) => upd('odmak od ivice', { inset: v })} />
          </div>
          <div className="prop-row">
            <label>Prečnik</label>
            <MmInput label="Prečnik nogica" value={legs.diameter} min={1} max={200} disabled={disabled} onCommit={(v) => upd('prečnik', { diameter: v })} />
          </div>
        </div>
      )}
      <p className="faint" style={{ fontSize: 11, margin: '4px 0 0' }}>Nogice podižu sklop za svoju visinu (s coklom dijele prostor ispod — računa se viša). Prednje nogice stoje iza cokle.</p>
    </div>
  );
}

/**
 * "Cokla i blende": a check per side (plinth below, fillers on top and on the sides) and, when on,
 * its size, whether it is added to the assembly or taken from it, its depth position and thickness.
 */
export function TrimProperties({ inst, exec, disabled, only }: { inst: AssemblyInstance; exec: Exec; disabled: boolean; only?: TrimSide }) {
  const set = (side: TrimSide, label: string, next: AssemblyTrim | null) =>
    exec(label, (d) => {
      const a = d.assemblies.find((x) => x.id === inst.id);
      if (a) setAssemblyTrim(a, side, next);
    });
  const sides = only ? [only] : TRIM_SIDES;
  return (
    <div className="prop-group">
      <h3>{only ? TRIM_LABEL[only] : 'Cokla i blende'}</h3>
      {sides.map((side) => {
        const t = inst.trims?.[side];
        const name = TRIM_LABEL[side];
        const upd = (label: string, patch: Partial<AssemblyTrim>) => t && set(side, `${name}: ${label}`, { ...t, ...patch });
        return (
          <div key={side} className="trim-block">
            <label className="opt-row trim-check">
              <input type="checkbox" checked={!!t} disabled={disabled} onChange={(e) => set(side, e.target.checked ? `${name}: uključena` : `${name}: isključena`, e.target.checked ? defaultTrim(side) : null)} />
              <strong>{name}</strong>
            </label>
            {t && (
              <div className="trim-fields">
                <div className="prop-row">
                  <label>{side === 'bottom' || side === 'top' ? 'Visina' : 'Širina'}</label>
                  <MmInput label={`${name}: mjera`} value={t.size} min={1} max={1000} disabled={disabled} onCommit={(v) => upd('mjera', { size: v })} />
                </div>
                <div className="prop-row">
                  <label title="Dodaje se: sklop postaje veći za ovu mjeru. Smanjuje sklop: vanjska mjera ostaje, ploče sklopa na toj strani se pomjere / skrate.">Mjera</label>
                  <select className="input" disabled={disabled} value={t.mode} onChange={(e) => upd(e.target.value === 'add' ? 'dodaje se na sklop' : 'smanjuje sklop', { mode: e.target.value as AssemblyTrim['mode'] })}>
                    <option value="add">dodaje se na sklop</option>
                    <option value="shrink">smanjuje sklop</option>
                  </select>
                </div>
                <div className="prop-row">
                  <label title="Koliko je prednja strana uvučena iza prednje strane sklopa (fronti); negativno = ispred">Uvlačenje (dubina)</label>
                  <MmInput label={`${name}: uvlačenje`} value={t.setback} min={-1000} max={1000} disabled={disabled} onCommit={(v) => upd('uvlačenje', { setback: v })} />
                </div>
                <div className="prop-row">
                  <label>Debljina</label>
                  <MmInput label={`${name}: debljina`} value={t.thickness} min={1} max={100} disabled={disabled} onCommit={(v) => upd('debljina', { thickness: v })} />
                </div>
              </div>
            )}
          </div>
        );
      })}
      <p className="faint" style={{ fontSize: 11, margin: '4px 0 0' }}>
        {only
          ? '„Dodaje se” povećava sklop; „smanjuje sklop” zadržava vanjsku mjeru, a ploče na toj strani se pomjere ili skrate.'
          : 'Cokla je ispod sklopa, blende iznad i sa strane. „Dodaje se” povećava sklop (cokla ga podigne); „smanjuje sklop” zadržava vanjsku mjeru, a ploče na toj strani se pomjere ili skrate.'}
      </p>
    </div>
  );
}
